import React, { useState, useEffect, useRef } from 'react';
import { userAppContext } from '../../context/Context';
import { useChatBot } from '../../hooks/useChatBot';
import { useChatStorage } from '../../hooks/useChatStorage';
import { useFeedbackConsent } from '../../hooks/useFeedbackConsent';
import './ChatWidget.css';

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: string;
  isConsentRequest?: boolean;
}

export default function ChatWidget() {
  const context = userAppContext();
  const isAuthenticated = context?.isAuthenticated || false;
  const userEmail = context?.user?.email || null;
  const [isOpen, setIsOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [pendingFeedbackMessage, setPendingFeedbackMessage] = useState<string | null>(null);
  const [pendingTicketKind, setPendingTicketKind] = useState<'feedback' | 'problem'>('feedback');
  const [isAwaitingGuestFeedbackEmail, setIsAwaitingGuestFeedbackEmail] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Custom hooks
  const { isLoading, sendMessage } = useChatBot(isAuthenticated, userEmail);
  const { messages, addMessage } = useChatStorage(isAuthenticated);
  const { feedbackConsent, saveFeedbackConsent } = useFeedbackConsent(isAuthenticated, userEmail);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleConsentResponse = async (consent: boolean) => {
    await saveFeedbackConsent(consent);

    const isProblem = pendingTicketKind === 'problem';
    const responseMessage: Message = {
      id: `bot-consent-response-${Date.now()}`,
      sender: 'bot',
      text: consent
        ? isProblem
          ? 'Thank you! Your report will include your email for follow-up.'
          : 'Thank you! Your suggestion will include your email for follow-up.'
        : isProblem
          ? 'Understood. Your issue will be sent anonymously. We will not be able to contact you about it.'
          : 'Understood. Your suggestion will be sent anonymously. We will not be able to contact you about it.',
      timestamp: new Date().toISOString(),
    };
    addMessage(responseMessage);

    if (pendingFeedbackMessage) {
      await sendFeedbackToBackend(pendingFeedbackMessage, consent ? userEmail : null, pendingTicketKind);
      setPendingFeedbackMessage(null);
    }
  };

  const sendFeedbackToBackend = async (
    message: string,
    overrideEmail?: string | null,
    ticketKind: 'feedback' | 'problem' = pendingTicketKind,
  ) => {
    try {
      const context = formatConversationContext(messages);
      const data = await sendMessage(message, {
        confirmFeedback: true,
        feedbackText: message,
        conversationContext: context,
        overrideUserEmail: overrideEmail ?? null,
        ticketKind,
      });
      if (!data) throw new Error('No response from server');

      const botMessage: Message = {
        id: `bot-${Date.now()}`,
        sender: 'bot',
        text: data.message,
        timestamp: new Date().toISOString(),
      };

      addMessage(botMessage);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: `bot-error-${Date.now()}`,
        sender: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
      };
      addMessage(errorMessage);
    }
  };

  const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isSkipEmailMessage = (value: string): boolean => /^(skip|no|omit|anonymous)$/i.test(value.trim());

  const handleSendMessage = async () => {
    const trimmedMessage = inputMessage.trim();
    if (!trimmedMessage || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: trimmedMessage,
      timestamp: new Date().toISOString(),
    };

    addMessage(userMessage);
    setInputMessage('');

    try {
      if (isAwaitingGuestFeedbackEmail) {
        if (!pendingFeedbackMessage) {
          setIsAwaitingGuestFeedbackEmail(false);
          return;
        }

        if (isSkipEmailMessage(trimmedMessage)) {
          addMessage({
            id: `bot-anonymous-warning-${Date.now()}`,
            sender: 'bot',
            text: 'Sending anonymously. We will not be able to contact you about this.',
            timestamp: new Date().toISOString(),
          });
          await sendFeedbackToBackend(pendingFeedbackMessage, null);
          setPendingFeedbackMessage(null);
          setIsAwaitingGuestFeedbackEmail(false);
          return;
        }

        if (!isValidEmail(trimmedMessage)) {
          addMessage({
            id: `bot-invalid-email-${Date.now()}`,
            sender: 'bot',
            text: 'Please enter a valid email address so we can send your request, or type "skip" to continue anonymously.',
            timestamp: new Date().toISOString(),
          });
          return;
        }

        await sendFeedbackToBackend(pendingFeedbackMessage, trimmedMessage);
        setPendingFeedbackMessage(null);
        setIsAwaitingGuestFeedbackEmail(false);
        return;
      }

      const messageWithContext = createMessageWithContext(trimmedMessage, [...messages, userMessage]);

      const data = await sendMessage(messageWithContext, { overrideUserEmail: null });
      if (!data) throw new Error('No response from server');

      if (data.isFeedback && isAuthenticated && feedbackConsent === null) {
        setPendingFeedbackMessage(trimmedMessage);
        setPendingTicketKind(data.ticketKind === 'problem' ? 'problem' : 'feedback');
        const isProblem = data.ticketKind === 'problem';
        const consentMessage: Message = {
          id: `bot-consent-${Date.now()}`,
          sender: 'bot',
          text: isProblem
            ? 'To help us follow up on this issue, would you like to include your email address? If you send it anonymously, we will not be able to contact you. You can change this preference anytime.'
            : 'To help us follow up on your suggestion, would you like to include your email address? If you send it anonymously, we will not be able to contact you. You can change this preference anytime.',
          timestamp: new Date().toISOString(),
          isConsentRequest: true,
        };
        addMessage(consentMessage);
      } else if (data.isFeedback && data.requiresEmail && !isAuthenticated) {
        setPendingFeedbackMessage(trimmedMessage);
        setPendingTicketKind(data.ticketKind === 'problem' ? 'problem' : 'feedback');
        setIsAwaitingGuestFeedbackEmail(true);
        const emailRequestMessage: Message = {
          id: `bot-email-request-${Date.now()}`,
          sender: 'bot',
          text: data.message,
          timestamp: new Date().toISOString(),
        };
        addMessage(emailRequestMessage);
      } else {
        const botMessage: Message = {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          text: data.message,
          timestamp: new Date().toISOString(),
        };
        addMessage(botMessage);

        if (data.isFeedback) {
          const emailForTicket = isAuthenticated && feedbackConsent === true ? userEmail : null;
          await sendFeedbackToBackend(
            trimmedMessage,
            emailForTicket,
            data.ticketKind === 'problem' ? 'problem' : 'feedback',
          );
        }
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: `bot-error-${Date.now()}`,
        sender: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
      };
      addMessage(errorMessage);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatMessageWithLinks = (text: string): string => {
    return text
      .replace(/\/([\w-]+)/g, match => {
        return `<a href="${match}" class="chat-link" target="_self">${match}</a>`;
      })
      .replace(/(https?:\/\/[^\s]+)/g, match => {
        return `<a href="${match}" class="chat-link" target="_blank" rel="noopener noreferrer">${match}</a>`;
      });
  };

  const formatConversationContext = (messages: Message[]): string => {
    const relevantMessages = messages.filter(msg => !msg.isConsentRequest && msg.id !== 'welcome').slice(-5);

    return relevantMessages
      .map(msg => {
        const time = formatTime(msg.timestamp);
        const sender = msg.sender === 'user' ? 'User' : 'Assistant';
        return `[${time}] ${sender}: ${msg.text}`;
      })
      .join('\n');
  };

  const createMessageWithContext = (originalMessage: string, currentMessages: Message[]): string => {
    const context = formatConversationContext(currentMessages);

    if (context.trim() === '') {
      return originalMessage;
    }

    const messageWithContext = `${originalMessage}\n\n--- Previous Conversation ---\n${context}`;
    return messageWithContext;
  };

  return (
    <>
      <div className={`chat-widget-container ${isOpen ? 'open' : ''}`}>
        {isOpen && (
          <div className="chat-widget-window">
            <div className="chat-widget-header">
              <div className="chat-widget-header-content">
                <i className="bi bi-robot"></i>
                <span>Support Bot</span>
              </div>
              <button className="chat-widget-close" onClick={() => setIsOpen(false)} aria-label="Close chat">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="chat-widget-disclaimer">
              <small className="text-muted">
                This assistant uses Groq-accelerated models to provide responses based on DISGENET website.
                The information generated may contain inaccuracies or hallucinations and should be
                independently verified.
              </small>
            </div>

            <div className="chat-widget-messages">
              {messages.map(msg => (
                <div key={msg.id} className={`chat-widget-message ${msg.sender}`}>
                  <div className="chat-widget-message-content">
                    <p dangerouslySetInnerHTML={{ __html: formatMessageWithLinks(msg.text) }} />
                    <span className="chat-widget-message-time">{formatTime(msg.timestamp)}</span>
                    {msg.isConsentRequest && (
                      <div className="chat-widget-consent-buttons">
                        <button
                          className="chat-widget-consent-btn accept"
                          onClick={() => handleConsentResponse(true)}
                        >
                          Yes, include my email
                        </button>
                        <button
                          className="chat-widget-consent-btn decline"
                          onClick={() => handleConsentResponse(false)}
                        >
                          No, send anonymously
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chat-widget-message bot">
                  <div className="chat-widget-message-content">
                    <div className="chat-widget-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-widget-input-container">
              <textarea
                className="chat-widget-input"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={1}
                disabled={isLoading}
              />
              <button
                className="chat-widget-send"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                aria-label="Send message"
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        className={`chat-widget-toggle ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Open chat"
      >
        <i className="bi bi-chat-dots-fill" style={{ fontSize: '20px' }}></i>
      </button>
    </>
  );
}
